import React, { useEffect, useState, useRef } from 'react';
import { X, FileText, Sparkles, Check, Loader2, ArrowRight } from 'lucide-react';
import type { Environment } from './RecentScans';
interface ReportConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  environment: Environment | null;
  onGenerate: (env: Environment, sections: string[]) => void;
}
const reportSections = [{
  id: 'executive-summary',
  label: 'Executive Summary',
  description: 'High-level overview of security posture and key findings'
}, {
  id: 'over-privileged',
  label: 'Over-Privileged Identities',
  description: 'Detailed breakdown of identities with excessive permissions'
}, {
  id: 'compliance',
  label: 'Compliance Posture',
  description: 'Framework adherence and compliance score analysis'
}, {
  id: 'recommendations',
  label: 'Remediation Steps',
  description: 'Prioritized actions with impact and effort ratings'
}, {
  id: 'identity-audit',
  label: 'Full Identity Audit',
  description: 'Complete inventory of identity types and risk levels'
}, {
  id: 'policy-review',
  label: 'Policy Review',
  description: 'Assessment of IAM policies and access controls'
}];
export function ReportConfigModal({
  isOpen,
  onClose,
  environment,
  onGenerate
}: ReportConfigModalProps) {
  const [selectedSections, setSelectedSections] = useState<string[]>(['executive-summary', 'over-privileged', 'compliance', 'recommendations']);
  const [generating, setGenerating] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  // Reset state and scroll to top when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedSections(['executive-summary', 'over-privileged', 'compliance', 'recommendations']);
      setGenerating(false);
      // Scroll to top
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }
  }, [isOpen, environment?.id]);
  if (!isOpen || !environment) return null;
  const toggleSection = (id: string) => {
    setSelectedSections(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const selectAll = () => {
    setSelectedSections(reportSections.map(s => s.id));
  };
  const deselectAll = () => {
    setSelectedSections([]);
  };
  const handleGenerate = () => {
    setGenerating(true);
    setTimeout(() => {
      setGenerating(false);
      onGenerate(environment, selectedSections);
      onClose();
    }, 1500);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-626">
      <div className="w-full max-w-[520px] max-h-[90vh] bg-astro-card border border-astro-border rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col" data-id="element-627">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-astro-border shrink-0" data-id="element-628">
          <div className="flex items-center gap-3" data-id="element-629">
            <div className="w-9 h-9 rounded-lg bg-astro-green/15 flex items-center justify-center" data-id="element-630">
              <FileText size={18} className="text-astro-green" data-id="element-631" />
            </div>
            <div data-id="element-632">
              <h2 className="text-lg font-bold text-white" data-id="element-633">Configure Report</h2>
              <p className="text-xs text-astro-text-secondary" data-id="element-634">
                {environment.clientName}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-astro-text-secondary hover:text-white transition-colors p-1" data-id="element-635">

            <X size={20} data-id="element-636" />
          </button>
        </div>

        {/* Content */}
        <div ref={contentRef} className="p-5 overflow-y-auto flex-1 min-h-0 space-y-5" data-id="element-637">

          <div data-id="element-638">
            <div className="flex items-center justify-between mb-1" data-id="element-639">
              <p className="text-sm text-astro-text-secondary" data-id="element-640">
                Select the sections to include in the report for{' '}
                <span className="text-white font-medium" data-id="element-641">
                  {environment.clientName}
                </span>
                .
              </p>
            </div>
            <div className="flex items-center gap-3 mt-2" data-id="element-642">
              <button onClick={selectAll} className="text-[11px] text-astro-green hover:text-astro-green-dark transition-colors font-medium" data-id="element-643">

                Select all
              </button>
              <span className="text-astro-border text-xs" data-id="element-644">•</span>
              <button onClick={deselectAll} className="text-[11px] text-astro-text-secondary hover:text-white transition-colors font-medium" data-id="element-645">

                Deselect all
              </button>
            </div>
          </div>

          {/* Section Checkboxes */}
          <div className="space-y-2" data-id="element-646">
            {reportSections.map(section => {
            const isSelected = selectedSections.includes(section.id);
            return <button key={section.id} onClick={() => toggleSection(section.id)} className={`w-full flex items-start gap-3 px-4 py-3 rounded-lg border transition-all text-left ${isSelected ? 'bg-astro-green/5 border-astro-green/25' : 'bg-[#0a0e14] border-astro-border hover:border-white/15'}`} data-id="element-647">

                  <div className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 mt-0.5 transition-colors ${isSelected ? 'bg-astro-green border-astro-green' : 'border-astro-border'}`} data-id="element-648">

                    {isSelected && <Check size={10} className="text-black" data-id="element-649" />}
                  </div>
                  <div data-id="element-650">
                    <span className={`text-sm font-medium block ${isSelected ? 'text-white' : 'text-astro-text-secondary'}`} data-id="element-651">

                      {section.label}
                    </span>
                    <span className="text-[11px] text-astro-text-secondary/60 leading-relaxed block mt-0.5" data-id="element-652">
                      {section.description}
                    </span>
                  </div>
                </button>;
          })}
          </div>

          {/* Client Info Summary */}
          <div className="bg-[#0a0e14] border border-astro-border rounded-lg p-4" data-id="element-653">
            <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider block mb-3" data-id="element-654">
              Report Summary
            </span>
            <div className="grid grid-cols-3 gap-3" data-id="element-655">
              <div data-id="element-656">
                <span className="text-[10px] text-astro-text-secondary block" data-id="element-657">
                  Environment
                </span>
                <span className="text-sm font-medium text-white" data-id="element-658">
                  {environment.type}
                </span>
              </div>
              <div data-id="element-659">
                <span className="text-[10px] text-astro-text-secondary block" data-id="element-660">
                  Compliance
                </span>
                <span className={`text-sm font-mono font-bold ${environment.complianceScore > 85 ? 'text-astro-green' : environment.complianceScore > 70 ? 'text-white' : 'text-astro-danger'}`} data-id="element-661">

                  {environment.complianceScore}%
                </span>
              </div>
              <div data-id="element-662">
                <span className="text-[10px] text-astro-text-secondary block" data-id="element-663">
                  Sections
                </span>
                <span className="text-sm font-mono font-bold text-white" data-id="element-664">
                  {selectedSections.length}/{reportSections.length}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-astro-border bg-astro-card shrink-0" data-id="element-665">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-666">

            Cancel
          </button>
          <button onClick={handleGenerate} disabled={generating || selectedSections.length === 0} className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-black bg-astro-green hover:bg-astro-green/90 rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.15)] disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-667">

            {generating ? <>
                <Loader2 size={16} className="animate-spin" data-id="element-668" />
                Generating…
              </> : <>
                <Sparkles size={16} data-id="element-669" />
                Generate Report
                <ArrowRight size={14} data-id="element-670" />
              </>}
          </button>
        </div>
      </div>
    </div>;
}